import { createAsyncThunk } from '@reduxjs/toolkit';
import { first, isObject } from 'lodash';
import { AxiosError } from 'axios';
import {
  AxiosErrorResponse, initPaging, ResponseResult, ResponseSingleResult,
} from '../../../../core/types/coreTypes';
import { PartRequestByLineFilters } from '../../../../stock/stock-part-requests/types/stockPartRequestTypes';
import { setLoading, setSuccessMessage, setWarningMessage } from '../../../slices/coreSlice';
import { stockBulkApi } from '../../../../api/stockBulkApi';
import { setStockPartRequestBulkFilters } from '../../../slices/stockPartRequestSlice';
import {
  BulkEditLocationRequest,
  BulkEditLocationResponse,
  BulkEditOwnerRequest,
  BulkEditOwnerResponse,
  PartRequestBulkItemDto,
  PartRequestBulkOptions,
  PartRequestBulkSelectionRequest,
  PartRequestBulkSelectionResponse,
  PartRequestCreateBulkRequest,
  PartRequestSubmitBulkRequest, PartRequestSubmitBulkResponse,
} from '../../../../stock/stock-bulk/types';
import { BulkRequestTypeEnum } from '../../../../stock/stock-bulk/BulkRequestTypeEnum';

export const createStockBulkOperation = createAsyncThunk<ResponseSingleResult, PartRequestCreateBulkRequest>(
  'post/createStockBulkOperation',
  async (request, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setLoading(true));
      // reset filters from the prev bulk operation
      dispatch(setStockPartRequestBulkFilters(initPaging));
      const response = await stockBulkApi.createBulkOperation(request.operation, request.filter);
      return response.data;
    } catch (error) {
      return rejectWithValue((error as AxiosErrorResponse).response?.data);
    } finally {
      dispatch(setLoading(false));
    }
  },
);

export const fetchStockBulkRequestItems = createAsyncThunk<
ResponseResult<PartRequestBulkItemDto[], PartRequestBulkOptions>,
{ filters: PartRequestByLineFilters, signal?: AbortSignal }
>(
  'get/fetchStockBulkRequestItems',
  async ({ filters, signal }, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setStockPartRequestBulkFilters(filters));
      dispatch(setLoading(true));
      const response = await stockBulkApi.fetchBulkRecords(filters, signal);
      return response.data;
    } catch (error) {
      return rejectWithValue((error as AxiosErrorResponse).response?.data);
    } finally {
      dispatch(setLoading(false));
    }
  },
);

export const updateStockBulkSelection = createAsyncThunk<
ResponseSingleResult<PartRequestBulkSelectionResponse>,
PartRequestBulkSelectionRequest
>(
  'post/updateStockBulkSelection',
  async (request, { rejectWithValue }) => {
    try {
      const response = await stockBulkApi.updateSelection(request);
      return response.data;
    } catch (error) {
      return rejectWithValue((error as AxiosErrorResponse).response?.data);
    }
  },
);

export const updateStockBulkLocation = createAsyncThunk<ResponseSingleResult<BulkEditLocationResponse>, BulkEditLocationRequest>(
  'post/updateStockBulkLocation',
  async (request, { rejectWithValue }) => {
    try {
      const response = await stockBulkApi.updateLocation(request);
      return response.data;
    } catch (error) {
      return rejectWithValue((error as AxiosErrorResponse).response?.data);
    }
  },
);

export const updateStockBulkOwner = createAsyncThunk<ResponseSingleResult<BulkEditOwnerResponse>, BulkEditOwnerRequest>(
  'post/updateStockBulkOwner',
  async (request, { rejectWithValue }) => {
    try {
      const response = await stockBulkApi.updateOwner(request);
      return response.data;
    } catch (error) {
      return rejectWithValue((error as AxiosErrorResponse).response?.data);
    }
  },
);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isAxiosError = (error: unknown): error is AxiosErrorResponse => isObject(error) && (error as any).isAxiosError;

export const submitStockBulk = createAsyncThunk<ResponseSingleResult<PartRequestSubmitBulkResponse>, PartRequestSubmitBulkRequest>(
  'post/submitStockBulk',
  async ({ operation }, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setLoading(true));

      const response = await stockBulkApi.submitBulkOperation(operation);
      const error = first(response.data.errors);
      if (error) {
        dispatch(setWarningMessage({ message: error.message, toastId: 'submitStockBulk' }));
      } else if (response.data.success) {
        const message = operation === BulkRequestTypeEnum.GeneratePurchaseOrder
          ? 'Purchase orders have been created successfully.'
          : 'Request lines have been processed successfully.';
        dispatch(setSuccessMessage({ message, toastId: 'submitStockBulk' }));
      }

      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        const serverError = first(error.response?.data?.errors);
        if (serverError) {
          dispatch(setWarningMessage({ message: serverError.message, toastId: 'submitStockBulk' }));
        }
      }
      return rejectWithValue((error as AxiosErrorResponse).response?.data);
    } finally {
      dispatch(setLoading(false));
    }
  },
);
